import groupMeetingIcon from "@/../../hypetrain/static/img/program/camera.svg";
import googleMeetIcon from "@/../../hypetrain/static/img/program/google-meet.svg";
import microsoftTeamsIcon from "@/../../hypetrain/static/img/program/microsoft-team.svg";
import zoomIcon from "@/../../hypetrain/static/img/program/zoom.svg";
import { ALL_PARTICIPANTS_OPTION } from "@/components/pages_logged_in/program/constants";
import { SimpleProgramUsersQuery } from "@/graphql";
import { MeetingPlatforms } from "@/types/programs";
import { SelectOption } from "@/types/select";
import { NonNullableArrayItem } from "@/types/utils";

type ProgramUser = NonNullableArrayItem<
  SimpleProgramUsersQuery["simpleProgramUsers"]
>;

/**
 * Converts participant data to options format for select components
 * @param participants - Array of participant objects
 * @param pendingParticipantEmails - A, ProgramUserrray of pending participant email strings
 * @returns Array of participant options
 * Generated by Copilot
 */
export const toParticipantOptions = (
  participants: ProgramUser[],
  pendingParticipantEmails: string[] = [],
): SelectOption[] => {
  try {
    let optionsList: SelectOption[] = participants.map((participant) => ({
      value: participant.id!,
      label:
        participant?.name && participant?.email
          ? `${participant.name} (${participant.email})`
          : participant?.name || participant?.email || "Unknown Participant",
    }));

    if (optionsList.length === 0) {
      // Pending value is negative to differentiate from existing participants
      optionsList = pendingParticipantEmails.map((email, index) => ({
        value: `-${index + 1}`,
        label: email,
      }));
    }
    return optionsList;
  } catch (error) {
    console.error("Error converting participants to options:", error);
    return [ALL_PARTICIPANTS_OPTION];
  }
};

/**
 * Checks if HTML string contains any non-empty text content
 * @param html - HTML string to check
 * @returns True if HTML contains non-empty text content, false otherwise
 * Generated by Copilot
 */
export const hasInnerText = (html: string): boolean => {
  if (!html) {
    return false;
  }

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").trim().length > 0;
  } catch (error) {
    console.error("Error parsing HTML:", error);
    return false;
  }
};

export const ALL_MEETING_PLATFORMS = [
  "Zoom",
  "GoogleMeet",
  "MicrosoftTeams",
  "Others",
] as const;

const PLATFORM_CONFIG: Record<
  MeetingPlatforms,
  {
    createLink: string;
    icon: string;
  }
> = {
  ["Zoom"]: {
    createLink: "https://app.zoom.us/wc/home",
    icon: zoomIcon,
  },
  ["GoogleMeet"]: {
    createLink: "https://meet.google.com/landing",
    icon: googleMeetIcon,
  },
  ["MicrosoftTeams"]: {
    createLink: "https://teams.live.com/",
    icon: microsoftTeamsIcon,
  },
  ["Others"]: {
    createLink: "",
    icon: groupMeetingIcon,
  },
};

const getPlatformConfig = (platform?: MeetingPlatforms) => {
  if (!platform) return PLATFORM_CONFIG["Others"];
  return PLATFORM_CONFIG[platform];
};

export const getMeetingPlatformIcon = (platform: MeetingPlatforms) =>
  getPlatformConfig(platform).icon;

export const getMeetingPlatformCreateHelpLink = (platform?: MeetingPlatforms) =>
  getPlatformConfig(platform).createLink;
